/**
 * Search, the ⌘K palette: one box that finds a ticket on any Team the reader
 * can see, by key, number or title words, finished and archived ones too.
 *
 * Results group by Team, the Team on screen first (`searchEveryTeam`), a few
 * each with "N more on <Team>", which opens that Team's queue with the same
 * search, where the Status filter lives. The palette has no filters of its
 * own: it finds and opens. ↑↓ move, ↵ opens the ticket in its Team's sheet
 * (`/desks/:id?item=`), ⌘↵ opens its Team with the search, Escape closes.
 * Opened by the shell's ⌘K, the rail's Search row, and a Team page whose
 * search found nothing there (with those words).
 */
import { useEffect, useId, useMemo, useState, type CSSProperties, type KeyboardEvent } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { parseWorkSearch } from "@yagni/shared";
import { StagePie } from "../ui/stagePie.js";
import { cx } from "../queue/primitives.js";
import { deskAccentToken } from "../desks/deskAccent.js";
import { rowKeyOf } from "../ticket/queueGroups.js";
import { STATUS_FILTER_LABEL } from "../ticket/statusFilter.js";
import { useWorkFeed } from "../work/useWorkFeed.js";
import { useTeamIdentity } from "../work/useTeamIdentity.js";
import { driverOf } from "../work/workViews.js";
import { pipelineStageOf, stagePieOf } from "../work/workStage.js";
import { searchEveryTeam, type SearchHit } from "./searchResults.js";
import { searchStore, useSearchState } from "./searchStore.js";
import "./searchPalette.css";

const TEAM_PATH = /^\/desks\/([^/]+)$/;

function findPath(deskId: string, query: string): string {
  return `/desks/${encodeURIComponent(deskId)}?${new URLSearchParams({ find: query })}`;
}

/** "Mobile has no ticket that matches." / "Mobile and Design have no ticket that matches." */
export function unmatchedTeamsLine(names: readonly string[]): string | null {
  if (names.length === 0) return null;
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${list} ${names.length === 1 ? "has" : "have"} no ticket that matches.`;
}

export function SearchPalette() {
  const { open, query } = useSearchState();
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => !next && searchStore.close()}>
      {open && <SearchPaletteBody initialQuery={query} />}
    </DialogPrimitive.Root>
  );
}

function SearchPaletteBody({ initialQuery }: { initialQuery: string }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const feed = useWorkFeed();
  const { roster } = useTeamIdentity();
  const [text, setText] = useState(initialQuery);
  const [selected, setSelected] = useState(0);
  const listId = useId();

  const onTeam = TEAM_PATH.exec(pathname)?.[1] ?? null;
  const search = useMemo(() => parseWorkSearch(text), [text]);
  const deskIds = useMemo(() => roster.map((team) => team.id), [roster]);
  const groups = useMemo(
    () =>
      search
        ? searchEveryTeam(feed.items, search, { deskIds, firstDeskId: onTeam, now: Date.now() })
        : [],
    [feed.items, search, deskIds, onTeam],
  );
  const flat = useMemo(() => groups.flatMap((group) => group.hits), [groups]);
  useEffect(() => setSelected(0), [text]);

  const nameOf = (deskId: string) => roster.find((team) => team.id === deskId)?.name ?? "This Team";
  const unmatched = unmatchedTeamsLine(
    roster
      .filter((team) => !groups.some((group) => group.deskId === team.id))
      .map((team) => team.name),
  );

  const go = (to: string) => {
    searchStore.close();
    navigate(to);
  };
  const openHit = (hit: SearchHit) => {
    const deskId = hit.row.anchor.deskId;
    if (!deskId) return;
    go(`/desks/${encodeURIComponent(deskId)}?${new URLSearchParams({ item: hit.row.anchor.id })}`);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (flat.length === 0) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setSelected((at) => (at + step + flat.length) % flat.length);
      return;
    }
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    event.preventDefault();
    const hit = flat[Math.min(selected, flat.length - 1)];
    if (!hit) return;
    const deskId = hit.row.anchor.deskId;
    if ((event.metaKey || event.ctrlKey) && deskId) go(findPath(deskId, text.trim()));
    else openHit(hit);
  };

  const optionId = (index: number) => `${listId}-${index}`;
  let index = -1;

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="spal-scrim" />
      <DialogPrimitive.Content
        className="spal"
        aria-describedby={undefined}
        data-testid="search-palette"
      >
        <DialogPrimitive.Title className="spal-title">Search</DialogPrimitive.Title>
        <div className="spal-box">
          <Search width={17} height={17} aria-hidden />
          <input
            role="combobox"
            aria-label="Search every Team"
            aria-expanded={flat.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={flat.length > 0 ? optionId(selected) : undefined}
            placeholder="Search every Team: a key, a number or words from a title"
            autoComplete="off"
            spellCheck={false}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={onKeyDown}
          />
          <kbd>esc</kbd>
        </div>
        <div className="spal-list" id={listId} role="listbox" aria-label="Tickets">
          {!search && (
            <p className="spal-note">
              Find a ticket on any Team by its key, its number or words from its title.
            </p>
          )}
          {feed.error && (
            <p className="spal-note" role="alert">
              Search could not read the Teams' tickets just now, so this list may be missing some.
              Try again in a moment.
            </p>
          )}
          {search && feed.ready && !feed.error && groups.length === 0 && (
            <p className="spal-note">
              No ticket on any Team matches. Finished and archived tickets were searched too.
            </p>
          )}
          {groups.map((group) => {
            const name = nameOf(group.deskId);
            const rest = group.total - group.hits.length;
            return (
              <div key={group.deskId} role="group" aria-label={name} className="spal-team">
                <div className="spal-th" role="presentation">
                  <span
                    className="tdot"
                    style={{ "--tc": deskAccentToken(group.deskId) } as CSSProperties}
                    aria-hidden
                  />
                  {name}
                  <span className="n">{group.total}</span>
                </div>
                {group.hits.map((hit) => {
                  index += 1;
                  const at = index;
                  const driver = driverOf(hit.row);
                  const { mode } = stagePieOf(driver);
                  return (
                    <div
                      key={hit.row.anchor.id}
                      id={optionId(at)}
                      role="option"
                      aria-selected={at === selected}
                      className={cx("spal-hit", at === selected && "sel")}
                      onMouseMove={() => setSelected(at)}
                      onClick={() => openHit(hit)}
                    >
                      <StagePie stage={pipelineStageOf(driver)} mode={mode} size={22} />
                      <span className="key">{rowKeyOf(hit.row.anchor)}</span>
                      <span className="t">{hit.row.anchor.title}</span>
                      <span className={cx("st", hit.status)}>
                        {STATUS_FILTER_LABEL[hit.status]}
                      </span>
                    </div>
                  );
                })}
                {rest > 0 && (
                  <Link
                    className="spal-more"
                    to={findPath(group.deskId, text.trim())}
                    onClick={() => searchStore.close()}
                  >
                    {rest} more on {name}
                  </Link>
                )}
              </div>
            );
          })}
          {/* A Team whose tickets never loaded has not been searched. */}
          {groups.length > 0 && unmatched && !feed.error && (
            <p className="spal-note quiet">{unmatched}</p>
          )}
        </div>
        <div className="spal-foot">
          <span>
            <kbd>↑↓</kbd> move · <kbd>↵</kbd> open · <kbd>⌘↵</kbd> open its Team with this search
          </span>
          <span>Filter by status on a Team's page</span>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
